
import { Link, useParams } from 'react-router-dom';
import SEO from '../../components/SEO';

const guidesData = {
  1: {
    id: 1,
    title: "Heights History Walk: Bungalows, Streetcars & Coffee Stops",
    description: "A 2-hour loop highlighting historic architecture, remnants of Houston's streetcar system, and local cafés.",
    duration: "2 hours",
    distance: "2.5 miles",
    accessibility: "Wheelchair accessible sidewalks",
    parking: "Street parking available on 19th Street",
    bestTime: "Morning or late afternoon",
    difficulty: "Easy",
    category: "History",
    image: "https://public.readdy.ai/ai/img_res/1f3ad0b26756a9f0980fbe0e7fa2db43.jpg",
    route: [
      {
        step: 1,
        title: "Heights Mercantile",
        description: "Start at this restored 1930s building, now housing shops and cafés. Learn about the Heights' transformation from farmland to Houston's first planned community.",
        context: "The Houston Heights was developed in 1891 as an independent city, complete with its own streetcar system connecting to downtown Houston.",
        location: "550 W 19th St"
      },
      {
        step: 2,
        title: "Historic Bungalow District",
        description: "Walk along tree-lined streets to see pristine examples of 1920s Craftsman bungalows and learn about the architectural styles that define the Heights.",
        context: "These homes represent the affordable housing boom of the 1920s, when working-class families could own homes with modern conveniences.",
        location: "Yale St & 18th St area"
      },
      {
        step: 3,
        title: "White Oak Bayou Coffee Stop",
        description: "Enjoy locally roasted coffee while overlooking the bayou. This spot showcases the Heights' relationship with Houston's waterways.",
        context: "White Oak Bayou was crucial to early settlement patterns and continues to shape neighborhood development today.",
        location: "Near White Oak Bayou Trail"
      },
      {
        step: 4,
        title: "Streetcar Remnants",
        description: "Discover traces of the historic Houston Heights streetcar line that connected this community to downtown from 1891 to 1905.",
        context: "The streetcar system made the Heights one of Houston's first successful suburbs, establishing a pattern of development that continues today.",
        location: "19th St & Heights Blvd"
      },
      {
        step: 5,
        title: "Heights Boulevard",
        description: "End at the wide, tree-lined boulevard that serves as the neighborhood's spine. Learn about early city planning and urban design.",
        context: "This 100-foot-wide boulevard with its central esplanade was revolutionary urban planning for 1891 Texas.",
        location: "Heights Boulevard"
      }
    ]
  },
  2: {
    id: 2,
    title: "Buffalo Bayou By Afternoon: Kid-Friendly Skyline Loop",
    description: "A 3-hour kid-friendly skyline loop through Houston's premier urban park with nature trails and city views.",
    duration: "3 hours",
    distance: "4.2 miles",
    accessibility: "Paved trails, stroller-friendly",
    parking: "Free parking at Buffalo Bayou Park",
    bestTime: "Afternoon (2-5 PM)",
    difficulty: "Easy",
    category: "Nature",
    image: "https://readdy.ai/api/search-image?query=Buffalo%20Bayou%20Park%20in%20Houston%20with%20downtown%20skyline%20view%2C%20families%20walking%20on%20trails%2C%20green%20natural%20spaces%2C%20afternoon%20light%2C%20kid-friendly%20outdoor%20setting%2C%20peaceful%20urban%20nature%20scene&width=800&height=500&seq=bayou-detail&orientation=landscape",
    route: [
      {
        step: 1,
        title: "Buffalo Bayou Park Visitor Center",
        description: "Begin at the visitor center to pick up trail maps and learn about the bayou's restoration. Kids can explore interactive exhibits about Houston's waterways.",
        context: "Buffalo Bayou Park represents one of the largest urban park restoration projects in the US, transforming 160 acres of neglected land into premier green space.",
        location: "1800 Allen Pkwy"
      },
      {
        step: 2,
        title: "Sabine Promenade",
        description: "Walk the elevated boardwalk offering stunning downtown views. Perfect photo opportunities with kids against the Houston skyline backdrop.",
        context: "This promenade showcases Houston's dramatic transformation from a small trading post to the fourth-largest city in America.",
        location: "Sabine St Bridge area"
      },
      {
        step: 3,
        title: "Lost Lake & Cistern",
        description: "Explore the reconstructed wetlands and historic cistern. Kids love spotting turtles, birds, and fish in this urban oasis.",
        context: "Lost Lake was recreated using historical maps from the 1800s, restoring natural habitat that existed before urban development.",
        location: "Near Waugh Dr Bridge"
      },
      {
        step: 4,
        title: "Waugh Bridge Bat Colony",
        description: "Visit North America's largest urban bat colony. Time your visit for sunset to see 300,000 bats emerge for their nightly hunt.",
        context: "Mexican free-tailed bats have roosted under this bridge since the 1980s, creating an unexpected wildlife spectacle in downtown Houston.",
        location: "Waugh Dr Bridge"
      },
      {
        step: 5,
        title: "Eleanor Tinsley Park",
        description: "End at this expansive park with playgrounds, picnic areas, and festival grounds. Kids can run freely while adults enjoy skyline views.",
        context: "Named after Houston's first female city council member, this park hosts major events including the Freedom Over Texas July 4th celebration.",
        location: "3600 Allen Pkwy"
      }
    ]
  },
  3: {
    id: 3,
    title: "East End Taquería Trail: Authentic Mexican Culinary Heritage",
    description: "6 authentic taco stops showcasing Houston's Mexican culinary heritage and neighborhood history.",
    duration: "4 hours",
    distance: "3.8 miles",
    accessibility: "Sidewalks available, some street crossings",
    parking: "Street parking and small lots",
    bestTime: "Late morning to early afternoon (11 AM - 3 PM)",
    difficulty: "Easy",
    category: "Food",
    image: "https://readdy.ai/api/search-image?query=Houston%20East%20End%20neighborhood%20with%20authentic%20Mexican%20taqueria%20storefronts%2C%20colorful%20murals%2C%20street%20food%20culture%2C%20vibrant%20community%20atmosphere%2C%20local%20flavor%20and%20cultural%20heritage&width=800&height=500&seq=eastend-detail&orientation=landscape",
    route: [
      {
        step: 1,
        title: "Villa Arcos",
        description: "Start with breakfast tacos at this family-owned institution serving the community since 1978. Try their famous barbacoa and fresh tortillas.",
        context: "Villa Arcos represents the first wave of Mexican restaurants that established the East End as Houston's Mexican food capital in the 1970s.",
        location: "6100 Harrisburg Blvd"
      },
      {
        step: 2,
        title: "Tacos Tierra Caliente",
        description: "Sample authentic street-style tacos from this truck that's been serving construction workers and locals for over 15 years.",
        context: "Food trucks like this one maintain the tradition of Mexican street food, serving recipes passed down through generations of immigrant families.",
        location: "Navigation Blvd & 75th St"
      },
      {
        step: 3,
        title: "El Tiempo Cantina",
        description: "Experience upscale Mexican dining at this landmark restaurant known for tableside guacamole and fajitas. Learn about the Laurenzo family legacy.",
        context: "The Laurenzo family pioneered Mexican fine dining in Houston, transforming perceptions of Mexican cuisine from fast food to culinary art.",
        location: "3130 Richmond Ave"
      },
      {
        step: 4,
        title: "Mercado del Sol",
        description: "Explore this Mexican market and try fresh tamales, aguas frescas, and traditional sweets while shopping for authentic ingredients.",
        context: "Mexican markets like this serve as community centers, preserving cultural traditions and providing ingredients for authentic home cooking.",
        location: "4508 Harrisburg Blvd"
      },
      {
        step: 5,
        title: "Ninfa's on Navigation",
        description: "Visit the original location of Houston's most famous Tex-Mex restaurant, birthplace of the fajita as we know it today.",
        context: "Ninfa Laurenzo invented the modern fajita here in 1973, creating a dish that would become synonymous with Houston's culinary identity.",
        location: "2704 Navigation Blvd"
      },
      {
        step: 6,
        title: "Xochi",
        description: "End with contemporary Oaxacan cuisine at this James Beard Award-winning restaurant, showcasing the evolution of Mexican food in Houston.",
        context: "Xochi represents the new generation of Mexican cuisine, elevating traditional Oaxacan recipes with modern techniques and local ingredients.",
        location: "1777 Walker St"
      }
    ]
  },
  4: {
    id: 4,
    title: "Museum District Cultural Walk: Art, Science & Architecture",
    description: "Explore Houston's renowned Museum District with stops at major institutions and hidden architectural gems.",
    duration: "5 hours",
    distance: "3.5 miles",
    accessibility: "Fully accessible, museum elevators available",
    parking: "Museum parking lots and street parking",
    bestTime: "Morning start (9 AM) for full day experience",
    difficulty: "Easy",
    category: "History",
    image: "https://readdy.ai/api/search-image?query=Houston%20Museum%20District%20with%20modern%20architecture%2C%20cultural%20institutions%2C%20tree-lined%20streets%2C%20visitors%20walking%20between%20museums%2C%20afternoon%20light%2C%20educational%20and%20cultural%20atmosphere&width=800&height=500&seq=museum-detail&orientation=landscape",
    route: [
      {
        step: 1,
        title: "Museum of Fine Arts Houston",
        description: "Begin at Texas's oldest art museum, featuring collections spanning 6,000 years. Don't miss the Cullen Sculpture Garden.",
        context: "Founded in 1900, MFAH was Houston's first major cultural institution, establishing the city's commitment to arts and culture.",
        location: "1001 Bissonnet St"
      },
      {
        step: 2,
        title: "Menil Collection",
        description: "Explore this intimate museum housing the private collection of John and Dominique de Menil, known for its serene atmosphere and diverse art.",
        context: "The Menil Collection represents one of the most important private art collections of the 20th century, emphasizing spiritual and cultural connections.",
        location: "1533 Sul Ross St"
      },
      {
        step: 3,
        title: "Rothko Chapel",
        description: "Experience this non-denominational chapel featuring 14 Mark Rothko paintings, designed as a space for meditation and reflection.",
        context: "Commissioned by the de Menils in 1964, the Rothko Chapel represents the intersection of art, spirituality, and human rights activism.",
        location: "3900 Yupon St"
      },
      {
        step: 4,
        title: "Houston Museum of Natural Science",
        description: "Discover dinosaurs, gems, and planetarium shows at one of the most visited museums in the United States.",
        context: "Founded in 1909, this museum has grown from a small natural history collection to a world-class institution attracting over 2 million visitors annually.",
        location: "5555 Hermann Park Dr"
      },
      {
        step: 5,
        title: "Contemporary Arts Museum Houston",
        description: "Explore cutting-edge contemporary art in this distinctive stainless steel building designed by Gunnar Birkerts.",
        context: "CAMH has been showcasing contemporary art since 1948, serving as a laboratory for new ideas and emerging artists.",
        location: "5216 Montrose Blvd"
      },
      {
        step: 6,
        title: "Rice University Campus",
        description: "End with a walk through Rice University's beautiful campus, featuring Byzantine-inspired architecture and the famous Sallyport.",
        context: "Founded in 1912 with oil money, Rice University's campus represents one of the finest examples of collegiate architecture in the South.",
        location: "6100 Main St"
      }
    ]
  },
  5: {
    id: 5,
    title: "Memorial Park Nature Loop: Urban Forest Adventure",
    description: "A comprehensive tour of Houston's largest urban park featuring trails, wildlife, and recreational areas.",
    duration: "3 hours",
    distance: "5.2 miles",
    accessibility: "Paved and unpaved trails, some elevation changes",
    parking: "Multiple parking areas throughout park",
    bestTime: "Early morning (7-10 AM) or late afternoon",
    difficulty: "Moderate",
    category: "Nature",
    image: "https://readdy.ai/api/search-image?query=Memorial%20Park%20Houston%20with%20running%20trails%2C%20towering%20trees%2C%20joggers%20and%20families%2C%20natural%20green%20spaces%2C%20golden%20hour%20lighting%2C%20peaceful%20urban%20forest%20setting&width=800&height=500&seq=memorial-detail&orientation=landscape",
    route: [
      {
        step: 1,
        title: "Memorial Park Conservancy Visitor Center",
        description: "Start at the visitor center to learn about the park's history and pick up trail maps. Explore exhibits about local wildlife and conservation efforts.",
        context: "Memorial Park was created in 1924 from the former Camp Logan military training facility, transforming 1,500 acres into Houston's premier urban park.",
        location: "6501 Memorial Dr"
      },
      {
        step: 2,
        title: "Arboretum & Nature Center",
        description: "Walk through 155 acres of native Texas forest, featuring over 300 native plant species and wildlife viewing opportunities.",
        context: "The Arboretum preserves the last remnant of the coastal prairie ecosystem that once covered the Houston area before urban development.",
        location: "4501 Woodway Dr"
      },
      {
        step: 3,
        title: "Buffalo Bayou Trail",
        description: "Follow the bayou trail through dense forest canopy, spotting herons, turtles, and other wildlife along the water's edge.",
        context: "Buffalo Bayou served as Houston's original highway, with Native Americans and early settlers using it for transportation and trade.",
        location: "Buffalo Bayou within Memorial Park"
      },
      {
        step: 4,
        title: "Memorial Park Golf Course",
        description: "Walk around the perimeter of this historic 18-hole course, one of the most challenging municipal courses in Texas.",
        context: "Built in 1936, the golf course was designed by John Bredemus and has hosted numerous professional tournaments and amateur championships.",
        location: "1001 E Memorial Loop Dr"
      },
      {
        step: 5,
        title: "Seymour Lieberman Exercise Trail",
        description: "Experience the famous 3-mile crushed granite running trail, popular with serious runners and casual walkers alike.",
        context: "Named after a local philanthropist, this trail has been a Houston institution since 1979, attracting runners from around the world.",
        location: "Memorial Loop Dr"
      },
      {
        step: 6,
        title: "Eastern Glades",
        description: "End at the newly restored Eastern Glades, featuring native prairie landscapes and stunning views of downtown Houston.",
        context: "The Eastern Glades restoration project removed invasive species and replanted native grasses, recreating the landscape that existed 200 years ago.",
        location: "East Memorial Park"
      }
    ]
  },
  6: {
    id: 6,
    title: "Montrose Art & Culture Tour: Creative Heart of Houston",
    description: "Discover Houston's creative heart through galleries, street art, and cultural landmarks in the Montrose area.",
    duration: "4 hours",
    distance: "3.2 miles",
    accessibility: "Sidewalks available, some gallery stairs",
    parking: "Street parking and small lots",
    bestTime: "Afternoon (1-5 PM) when galleries are open",
    difficulty: "Easy",
    category: "History",
    image: "https://readdy.ai/api/search-image?query=Montrose%20Houston%20neighborhood%20with%20art%20galleries%2C%20colorful%20street%20murals%2C%20creative%20cultural%20spaces%2C%20people%20exploring%20art%20venues%2C%20vibrant%20artistic%20community%20atmosphere&width=800&height=500&seq=montrose-detail&orientation=landscape",
    route: [
      {
        step: 1,
        title: "Menil Collection & Neighborhood",
        description: "Start in the Menil neighborhood, exploring the main collection and surrounding bungalows converted into art spaces.",
        context: "The de Menil family transformed this residential area into an art district, purchasing and renovating homes to create a unique cultural campus.",
        location: "1533 Sul Ross St"
      },
      {
        step: 2,
        title: "Richmond Avenue Gallery District",
        description: "Walk Richmond Avenue to visit multiple contemporary galleries showcasing local and international artists.",
        context: "Richmond Avenue became Houston's gallery row in the 1980s, establishing Montrose as the city's primary arts district.",
        location: "Richmond Ave between Shepherd & Montrose"
      },
      {
        step: 3,
        title: "Montrose Boulevard Murals",
        description: "Discover vibrant street art and murals along Montrose Boulevard, including works by local and international artists.",
        context: "Montrose's mural scene reflects the neighborhood's history as a bohemian enclave and its ongoing role as Houston's creative center.",
        location: "Montrose Blvd"
      },
      {
        step: 4,
        title: "Westheimer Cultural District",
        description: "Explore eclectic shops, vintage stores, and cultural venues along Westheimer Road, the heart of Montrose's alternative culture.",
        context: "Westheimer Road has been Montrose's main commercial strip since the 1970s, evolving from counterculture hub to diverse cultural corridor.",
        location: "Westheimer Rd"
      },
      {
        step: 5,
        title: "Cherryhurst Park",
        description: "Visit this small neighborhood park that often features outdoor art installations and community events.",
        context: "Cherryhurst Park serves as a community gathering space, hosting art markets, festivals, and cultural events that define Montrose's character.",
        location: "1700 Cherryhurst St"
      },
      {
        step: 6,
        title: "Poison Girl & Local Music Venues",
        description: "End your tour exploring the music venues that have made Montrose Houston's live music capital, from punk to indie rock.",
        context: "Montrose's music scene has launched numerous bands and continues to be Houston's primary destination for alternative and independent music.",
        location: "1641 Westheimer Rd"
      }
    ]
  },
  7: {
    id: 7,
    title: "Halloween Heights Ghost Walk: Spooky Stories & Historic Hauntings",
    description: "A spine-tingling evening tour through the Heights' most haunted locations, featuring local ghost stories and historic Halloween traditions.",
    duration: "2 hours",
    distance: "2 miles",
    accessibility: "Well-lit sidewalks, some uneven surfaces",
    parking: "Street parking available on 19th Street",
    bestTime: "Evening after sunset (7-9 PM)",
    difficulty: "Easy",
    category: "History",
    image: "https://readdy.ai/api/search-image?query=Houston%20Heights%20neighborhood%20at%20twilight%20during%20Halloween%20season%20with%20historic%20bungalow%20houses%20decorated%20for%20Halloween%2C%20orange%20autumn%20leaves%2C%20spooky%20decorations%2C%20jack-o-lanterns%20on%20porches%2C%20evening%20atmosphere%20with%20warm%20street%20lights%2C%20family-friendly%20Halloween%20vibes&width=800&height=500&seq=halloween-heights-detail&orientation=landscape",
    route: [
      {
        step: 1,
        title: "Heights Mercantile - The Phantom Shopper",
        description: "Begin your ghostly journey at this historic building where staff report mysterious footsteps and unexplained cold spots after closing time.",
        context: "Built in the 1930s, this building has housed countless businesses. Locals whisper about a well-dressed figure who appears in the windows long after the shops have closed.",
        location: "550 W 19th St"
      },
      {
        step: 2,
        title: "The Weeping Bungalow",
        description: "Visit the famous 1920s bungalow where residents report hearing a woman's cries on moonless nights, believed to be mourning a love lost to the 1918 flu pandemic.",
        context: "During the Spanish flu outbreak, this house was a makeshift care center. The ghostly woman is said to be Sarah Mitchell, who lost her fiancé and never recovered from her grief.",
        location: "Yale St & 18th St area"
      },
      {
        step: 3,
        title: "White Oak Bayou Bridge - The Lost Conductor",
        description: "Stand on the old bridge where the phantom streetcar conductor still searches for passengers who never made it home from their Halloween festivities in 1903.",
        context: "On Halloween night 1903, a streetcar derailed near this spot during a terrible storm. The conductor, Thomas Harper, died trying to save his passengers and is said to still walk these tracks.",
        location: "White Oak Bayou Bridge"
      },
      {
        step: 4,
        title: "The Haunted Heights Cemetery Gates",
        description: "Pass by the old cemetery gates where Halloween pranksters from the 1940s still play their ghostly tricks, moving decorations and rearranging jack-o'-lanterns.",
        context: "Local teenagers in the 1940s would dare each other to spend Halloween night in the cemetery. Three boys went missing one Halloween and were found the next morning with no memory of the night.",
        location: "Heights Cemetery entrance"
      },
      {
        step: 5,
        title: "Heights Boulevard - The Halloween Parade of Spirits",
        description: "End at the grand boulevard where witnesses claim to see a ghostly Halloween parade from the 1920s, complete with costumed children and horse-drawn carriages.",
        context: "Every Halloween from 1920-1929, the Heights hosted elaborate parades down this boulevard. After the stock market crash, the tradition ended, but some say the spirits continue their eternal celebration.",
        location: "Heights Boulevard"
      }
    ]
  },
  8: {
    id: 8,
    title: "Buffalo Bayou Fall Foliage Tour: Autumn Colors & Wildlife",
    description: "Experience Houston's autumn colors along the bayou with seasonal wildlife viewing, harvest activities, and fall photography spots.",
    duration: "3 hours",
    distance: "4.5 miles",
    accessibility: "Paved trails, wheelchair accessible",
    parking: "Free parking at multiple bayou access points",
    bestTime: "Late afternoon (3-6 PM) for best lighting",
    difficulty: "Easy",
    category: "Nature",
    image: "https://readdy.ai/api/search-image?query=Buffalo%20Bayou%20Park%20Houston%20in%20autumn%20with%20colorful%20fall%20foliage%2C%20families%20walking%20on%20trails%20with%20children%20in%20light%20jackets%2C%20golden%20and%20orange%20leaves%20on%20trees%2C%20downtown%20Houston%20skyline%20in%20background%2C%20crisp%20fall%20weather%2C%20seasonal%20beauty&width=800&height=500&seq=bayou-fall-detail&orientation=landscape",
    route: [
      {
        step: 1,
        title: "Sabine to Bagby Promenade",
        description: "Start at the Sabine Promenade and walk toward Bagby, enjoying the changing leaves of native Texas trees against the downtown skyline.",
        context: "Fall in Houston brings subtle but beautiful changes to native trees like bald cypress, sweet gum, and red oak, creating a unique subtropical autumn experience.",
        location: "Sabine St Bridge"
      },
      {
        step: 2,
        title: "Lost Lake Autumn Reflections",
        description: "Capture stunning reflections of fall foliage in Lost Lake while observing migrating waterfowl returning for winter.",
        context: "Lost Lake serves as a crucial stopover for migratory birds traveling the Central Flyway, with peak activity occurring during fall migration season.",
        location: "Near Waugh Dr Bridge"
      },
      {
        step: 3,
        title: "Cistern Fall Photography",
        description: "Visit the historic cistern surrounded by changing leaves, perfect for autumn photography with dramatic lighting through the trees.",
        context: "The 1926 cistern, once part of Houston's water system, now serves as a unique architectural element enhanced by seasonal foliage changes.",
        location: "Buffalo Bayou Cistern"
      },
      {
        step: 4,
        title: "Waugh Bridge Bat Colony Autumn Viewing",
        description: "Experience the bat emergence during fall migration season, when the colony is most active before winter dormancy.",
        context: "Fall marks peak activity for the Mexican free-tailed bats as they prepare for winter, making this the best season for viewing the spectacular emergence.",
        location: "Waugh Dr Bridge"
      },
      {
        step: 5,
        title: "Eleanor Tinsley Park Fall Festival Area",
        description: "End at Eleanor Tinsley Park, often hosting fall festivals and harvest celebrations with seasonal activities for families.",
        context: "The park's open spaces and mature trees create perfect conditions for fall festivals, continuing Houston's tradition of outdoor autumn celebrations.",
        location: "3600 Allen Pkwy"
      }
    ]
  },
  9: {
    id: 9,
    title: "Houston Pumpkin Patch & Farm Trail: Harvest Adventures",
    description: "Visit authentic working farms around Houston for pumpkin picking, corn mazes, and fall harvest experiences perfect for families.",
    duration: "4 hours",
    distance: "30 miles driving",
    accessibility: "Mostly accessible, some gravel paths",
    parking: "Free on-site parking at all locations",
    bestTime: "Weekend mornings (9 AM - 1 PM)",
    difficulty: "Easy",
    category: "Nature",
    image: "https://readdy.ai/api/search-image?query=Houston%20area%20pumpkin%20patch%20with%20families%20picking%20pumpkins%2C%20orange%20pumpkins%20scattered%20across%20farm%20fields%2C%20corn%20maze%20in%20background%2C%20children%20playing%20in%20autumn%20setting%2C%20harvest%20festival%20atmosphere%20with%20hay%20bales%20and%20fall%20decorations&width=800&height=500&seq=pumpkin-patch-detail&orientation=landscape",
    route: [
      {
        step: 1,
        title: "Dewberry Farm - The Pumpkin Kingdom",
        description: "Start your harvest adventure at this 23-acre working farm featuring over 90 activities including pumpkin picking, corn maze, and hayrides.",
        context: "Established in 1996, Dewberry Farm has grown from a small produce stand to Houston's premier agritourism destination, maintaining its commitment to authentic farm experiences.",
        location: "7705 Barker Cypress Rd, Brookshire"
      },
      {
        step: 2,
        title: "Pasadena Pumpkin Patch - Traditional Family Fun",
        description: "Experience old-fashioned pumpkin picking at this family-owned farm with wagon rides through the pumpkin fields and a petting zoo.",
        context: "Operating since 1985, this third-generation family farm specializes in growing pumpkins specifically for picking, with varieties ranging from tiny decorative gourds to giant carving pumpkins.",
        location: "3902 Red Bluff Rd, Pasadena"
      },
      {
        step: 3,
        title: "Traders Village Fall Festival - Market & Maze",
        description: "Combine pumpkin shopping with a large outdoor market featuring local crafts, food vendors, and a challenging corn maze designed with Texas themes.",
        context: "Since 1974, Traders Village has been Houston's largest weekend flea market. Their fall festival adds seasonal activities while maintaining the authentic market atmosphere.",
        location: "7979 N Eldridge Pkwy, Houston"
      },
      {
        step: 4,
        title: "Froberg's Farm - Pick Your Perfect Pumpkin",
        description: "Visit this century-old family farm for u-pick pumpkins, fresh apple cider, and homemade pies in their country store.",
        context: "The Froberg family has been farming this land since 1906. Their fall harvest celebrates four generations of sustainable farming practices and community connection.",
        location: "1120 Ave G, Alvin"
      },
      {
        step: 5,
        title: "Maize South Texas - The Great Corn Adventure",
        description: "End your farm trail at this elaborate corn maze featuring different themes each year, plus pumpkin cannons and farm animal encounters.",
        context: "Created on working farmland, this attraction showcases modern agriculture while providing entertainment. The corn maze design changes annually, often featuring Houston landmarks or Texas history.",
        location: "2936 FM 1093, Sealy"
      }
    ]
  },
  10: {
    id: 10,
    title: "Downtown Houston Halloween Historic Tour: Ghostly Urban Legends",
    description: "Explore downtown's spookiest stories and haunted buildings, from the Rice Hotel to Main Street's ghostly past.",
    duration: "2.5 hours",
    distance: "2.8 miles",
    accessibility: "Paved sidewalks, some steps at historic buildings",
    parking: "Paid parking garages available",
    bestTime: "Evening hours (6-8:30 PM)",
    difficulty: "Easy",
    category: "History",
    image: "https://readdy.ai/api/search-image?query=Downtown%20Houston%20at%20dusk%20during%20Halloween%20season%20with%20historic%20buildings%20lit%20dramatically%2C%20vintage%20architecture%20creating%20spooky%20shadows%2C%20urban%20Halloween%20atmosphere%20with%20decorated%20storefronts%2C%20pedestrians%20in%20light%20Halloween%20costumes%2C%20moody%20evening%20lighting&width=800&height=500&seq=downtown-halloween-detail&orientation=landscape",
    route: [
      {
        step: 1,
        title: "The Rice Hotel - Houston's Most Haunted",
        description: "Begin at the legendary Rice Hotel, where guests still report encounters with spirits from the hotel's glamorous and tragic past.",
        context: "Opened in 1913, the Rice Hotel hosted presidents, celebrities, and oil barons. Room 440 is said to be haunted by a woman in white who died under mysterious circumstances in the 1920s.",
        location: "909 Texas Ave"
      },
      {
        step: 2,
        title: "Main Street - The Phantom Streetcar",
        description: "Walk historic Main Street where witnesses claim to see ghostly streetcars from Houston's early transit system still making their rounds.",
        context: "From 1874 to 1940, streetcars were Houston's primary transportation. The last conductor, Jake Morrison, died at his post in 1940 and some say he's still trying to complete his final route.",
        location: "Main Street between Texas & Preston"
      },
      {
        step: 3,
        title: "Julia Ideson Building - The Librarian's Vigil",
        description: "Visit Houston's original central library where late-night security guards report books mysteriously moving and the sound of typing from empty rooms.",
        context: "Julia Ideson, Houston's first professional librarian, dedicated her life to this building from 1903 until her death in 1945. Staff believe she continues watching over her beloved books.",
        location: "500 McKinney St"
      },
      {
        step: 4,
        title: "Market Square Park - The Halloween Hangings",
        description: "Stand in the park where Houston's old courthouse once held public executions, and locals report seeing shadowy figures on Halloween nights.",
        context: "From 1840 to 1910, this was the site of Harris County's courthouse and jail. Several public hangings took place here, and the area maintains an eerie atmosphere after dark.",
        location: "301 Milam St"
      },
      {
        step: 5,
        title: "The Majestic Metro - Vaudeville Spirits",
        description: "End at the historic Majestic Theatre where performers from the vaudeville era are said to still take the stage during Halloween performances.",
        context: "Built in 1911 as a vaudeville house, the Majestic hosted traveling shows and local acts. Several performers died here over the decades, and their presence is still felt during evening shows.",
        location: "615 Preston St"
      }
    ]
  }
};

export default function GuideDetail() {
  const { id } = useParams();
  const guideId = parseInt(id || '1');
  
  // Get guide data or default to guide 1
  const guide = guidesData[guideId as keyof typeof guidesData] || guidesData[1];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "TouristAttraction",
    "name": guide.title,
    "description": guide.description,
    "url": `${import.meta.env.VITE_SITE_URL}/guides/${id}`,
    "touristType": "Cultural Heritage",
    "isAccessibleForFree": true,
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "29.7821",
      "longitude": "-95.4107"
    },
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Houston",
      "addressRegion": "Texas",
      "addressCountry": "US"
    },
    "author": {
      "@type": "Person",
      "name": "Eric Javidi"
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <SEO
        title={`${guide.title} - Eric Javidi`}
        description={`${guide.description} Duration: ${guide.duration}. ${guide.accessibility}. ${guide.bestTime}.`}
        keywords={`Heights Houston, Houston history walk, historic architecture, streetcar Houston, bungalow district, Heights Mercantile, Halloween Houston, ghost walk, haunted Houston`}
        ogTitle={`${guide.title} - Eric Javidi`}
        ogDescription={guide.description}
        ogUrl={`${import.meta.env.VITE_SITE_URL}/guides/${id}`}
        twitterTitle={`${guide.title} - Eric Javidi`}
        twitterDescription={guide.description}
        jsonLd={jsonLd}
      />

      {/* Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="text-xl font-semibold text-neutral-800">
                Eric Javidi
              </Link>
            </div>
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-8">
                <Link to="/" className="text-neutral-600 hover:text-green-600 px-3 py-2 text-sm font-medium">
                  Home
                </Link>
                <Link to="/about" className="text-neutral-600 hover:text-green-600 px-3 py-2 text-sm font-medium">
                  About
                </Link>
                <Link to="/guides" className="text-neutral-600 hover:text-green-600 px-3 py-2 text-sm font-medium">
                  Guides
                </Link>
                <Link to="/fall-festivals" className="text-neutral-600 hover:text-green-600 px-3 py-2 text-sm font-medium">
                  Fall Festivals
                </Link>
                <Link to="/resources" className="text-neutral-600 hover:text-green-600 px-3 py-2 text-sm font-medium">
                  Resources
                </Link>
                <Link to="/contact" className="text-neutral-600 hover:text-green-600 px-3 py-2 text-sm font-medium">
                  Contact
                </Link>
              </div>
            </div>
            <div className="md:hidden">
              <button className="text-neutral-600 hover:text-neutral-800">
                <i className="ri-menu-line text-xl"></i>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center space-x-2 text-sm">
            <Link to="/guides" className="text-green-600 hover:text-green-800">Guides</Link>
            <i className="ri-arrow-right-s-line text-neutral-400"></i>
            <span className="text-neutral-600">{guide.title.split(':')[0]}</span>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-neutral-800 mb-4">
                {guide.title}
              </h1>
              <p className="text-lg text-neutral-600 mb-8">
                {guide.description}
              </p>
              
              {/* At-a-Glance */}
              <div className="bg-neutral-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-neutral-800 mb-4">At-a-Glance</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <i className="ri-time-line text-green-600 mr-2"></i>
                    <span className="text-sm text-neutral-600">{guide.duration}</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-route-line text-green-600 mr-2"></i>
                    <span className="text-sm text-neutral-600">{guide.distance}</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-wheelchair-line text-green-600 mr-2"></i>
                    <span className="text-sm text-neutral-600">{guide.accessibility}</span>
                  </div>
                  <div className="flex items-center">
                    <i className="ri-parking-line text-green-600 mr-2"></i>
                    <span className="text-sm text-neutral-600">{guide.parking}</span>
                  </div>
                  <div className="flex items-center col-span-2">
                    <i className={guide.id === 7 || guide.id === 10 ? "ri-moon-line text-green-600 mr-2" : "ri-sun-line text-green-600 mr-2"}></i>
                    <span className="text-sm text-neutral-600">Best time: {guide.bestTime}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <div className="w-full h-96 bg-cover bg-center rounded-lg" style={{
                backgroundImage: `url('${guide.image}')`
              }}></div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-12 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-neutral-800 mb-6">Route Map</h2>
          <div className="bg-white rounded-lg overflow-hidden shadow-sm">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3463.234567890123!2d-95.4107!3d29.7821!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjnCsDQ2JzU1LjYiTiA5NcKwMjQnMzguNSJX!5e0!3m2!1sen!2sus!4v1234567890123"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={`${guide.title} Route`}
            ></iframe>
          </div>
        </div>
      </section>

      {/* Route Details */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-neutral-800 mb-8">Route Details</h2>
          <div className="space-y-8">
            {guide.route.map((stop, index) => (
              <div key={stop.step} className="flex">
                <div className="flex-shrink-0 w-10 h-10 bg-green-600 text-white rounded-full flex items-center justify-center font-semibold mr-6">
                  {stop.step}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-neutral-800 mb-2">
                    {stop.title}
                  </h3>
                  <p className="text-neutral-600 mb-3">
                    {stop.description}
                  </p>
                  <div className={`p-4 rounded-lg mb-3 ${guide.id === 7 || guide.id === 10 ? 'bg-purple-50' : 'bg-green-50'}`}>
                    <h4 className={`font-medium mb-2 ${guide.id === 7 || guide.id === 10 ? 'text-purple-800' : 'text-green-800'}`}>
                      {guide.id === 7 || guide.id === 10 ? 'Ghostly Legend' : 'Historical Context'}
                    </h4>
                    <p className={`text-sm ${guide.id === 7 || guide.id === 10 ? 'text-purple-700' : 'text-green-700'}`}>
                      {stop.context}
                    </p>
                  </div>
                  <div className="flex items-center text-sm text-neutral-500">
                    <i className="ri-map-pin-line mr-1"></i>
                    <span>{stop.location}</span>
                  </div>
                  {index < guide.route.length - 1 && (
                    <div className="mt-6 border-l-2 border-green-200 h-8 ml-4"></div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Notes */}
      <section className="py-12 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-neutral-800 mb-6">Good to Know</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {(guide.id === 7 || guide.id === 10) ? (
              <>
                <div className="bg-white p-6 rounded-lg">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-flashlight-line text-purple-600"></i>
                  </div>
                  <h3 className="font-semibold text-neutral-800 mb-2">Bring a Flashlight</h3>
                  <p className="text-sm text-neutral-600">
                    Evening tour with some darker areas. Small flashlight recommended for safety and atmosphere.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-lg">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-group-line text-orange-600"></i>
                  </div>
                  <h3 className="font-semibold text-neutral-800 mb-2">Family-Friendly Frights</h3>
                  <p className="text-sm text-neutral-600">
                    Spooky but not scary - designed for all ages. Perfect introduction to Houston's ghost stories.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-lg">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-calendar-line text-red-600"></i>
                  </div>
                  <h3 className="font-semibold text-neutral-800 mb-2">Best in October</h3>
                  <p className="text-sm text-neutral-600">
                    Peak season for decorations and Halloween atmosphere. Available year-round with different seasonal touches.
                  </p>
                </div>
              </>
            ) : guide.id === 9 ? (
              <>
                <div className="bg-white p-6 rounded-lg">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-car-line text-orange-600"></i>
                  </div>
                  <h3 className="font-semibold text-neutral-800 mb-2">Driving Required</h3>
                  <p className="text-sm text-neutral-600">
                    This trail requires driving between locations. Plan for gas and consider carpooling for groups.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-lg">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-money-dollar-circle-line text-green-600"></i>
                  </div>
                  <h3 className="font-semibold text-neutral-800 mb-2">Admission Fees</h3>
                  <p className="text-sm text-neutral-600">
                    Most farms charge admission ($10-25 per person). Some activities may have additional costs.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-lg">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-calendar-check-line text-blue-600"></i>
                  </div>
                  <h3 className="font-semibold text-neutral-800 mb-2">Seasonal Hours</h3>
                  <p className="text-sm text-neutral-600">
                    Most farms operate September-November. Check individual farm websites for current hours and activities.
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="bg-white p-6 rounded-lg">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-contrast-2-line text-blue-600"></i>
                  </div>
                  <h3 className="font-semibold text-neutral-800 mb-2">Shade &amp; Comfort</h3>
                  <p className="text-sm text-neutral-600">
                    Most of the route is shaded by mature trees. Bring water and comfortable walking shoes.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-lg">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-restaurant-line text-green-600"></i>
                  </div>
                  <h3 className="font-semibold text-neutral-800 mb-2">Food &amp; Restrooms</h3>
                  <p className="text-sm text-neutral-600">
                    Multiple cafés and restaurants along the route. Public restrooms available at major stops.
                  </p>
                </div>
                
                <div className="bg-white p-6 rounded-lg">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                    <i className="ri-add-line text-purple-600"></i>
                  </div>
                  <h3 className="font-semibold text-neutral-800 mb-2">Optional Add-ons</h3>
                  <p className="text-sm text-neutral-600">
                    Extend your visit with nearby attractions or explore additional neighborhoods in the area.
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </section>

      {/* Related Guides */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-bold text-neutral-800 mb-8">More Houston Guides</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Link to="/guides/2" className="bg-neutral-50 rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer">
              <div className="h-32 bg-cover bg-center" style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Buffalo%20Bayou%20Park%20in%20Houston%20with%20downtown%20skyline%20view%2C%20families%20walking%20on%20trails%2C%20green%20natural%20spaces%2C%20afternoon%20light%2C%20kid-friendly%20outdoor%20setting%2C%20peaceful%20urban%20nature%20scene&width=300&height=200&seq=bayou-related&orientation=landscape')`
              }}></div>
              <div className="p-4">
                <h3 className="font-semibold text-neutral-800 mb-1">Buffalo Bayou By Afternoon</h3>
                <p className="text-sm text-neutral-600">3 hrs, kid-friendly skyline loop</p>
              </div>
            </Link>
            
            <Link to="/guides/3" className="bg-neutral-50 rounded-lg overflow-hidden hover:shadow-md transition-shadow cursor-pointer">
              <div className="h-32 bg-cover bg-center" style={{
                backgroundImage: `url('https://readdy.ai/api/search-image?query=Houston%20East%20End%20neighborhood%20with%20authentic%20Mexican%20taqueria%20storefronts%2C%20colorful%20murals%2C%20street%20food%20culture%2C%20vibrant%20community%20atmosphere%2C%20local%20flavor%20and%20cultural%20heritage&width=300&height=200&seq=eastend-related&orientation=landscape')`
              }}></div>
              <div className="p-4">
                <h3 className="font-semibold text-neutral-800 mb-1">East End Taquería Trail</h3>
                <p className="text-sm text-neutral-600">6 stops, local flavor + history</p>
              </div>
            </Link>
            
            <Link to="/guides" className="bg-green-50 rounded-lg p-6 flex items-center justify-center text-center hover:bg-green-100 transition-colors cursor-pointer">
              <div>
                <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-arrow-right-line text-white"></i>
                </div>
                <h3 className="font-semibold text-green-800 mb-1">View All Guides</h3>
                <p className="text-sm text-green-600">Discover more Houston stories</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <h3 className="text-xl font-semibold mb-4">Eric Javidi</h3>
              <p className="text-neutral-300 mb-4">
                Houston-based storyteller exploring where the city's history meets its natural landscapes.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/guides" className="text-neutral-300 hover:text-white">Guides</Link></li>
                <li><Link to="/fall-festivals" className="text-neutral-300 hover:text-white">Fall Festivals</Link></li>
                <li><Link to="/resources" className="text-neutral-300 hover:text-white">Resources</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Connect</h4>
              <ul className="space-y-2">
                <li><a href="https://readdy.ai/?origin=logo" className="text-neutral-300 hover:text-white">Made with Readdy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-neutral-700 mt-8 pt-8 text-center">
            <p className="text-neutral-400">
              © 2024 Eric Javidi. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
